import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Play, Trash2, Edit3, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import BackingTrackPanel from "@/components/BackingTrackPanel";
import AiAnalysisPanel from "@/components/AiAnalysisPanel";
import BackButton from "@/components/BackButton";

export default function SongDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [song, setSong] = useState(null);

  const loadSong = useCallback(async () => {
    try { const { data } = await api.get(`/songs/${id}`); setSong(data); }
    catch (e) { toast.error(formatApiError(e)); nav("/library"); }
  }, [id, nav]);

  useEffect(() => { loadSong(); }, [loadSong]);

  const remove = async () => {
    try { await api.delete(`/songs/${id}`); toast.success("Song deleted."); nav("/library"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!song) return <main className="max-w-3xl mx-auto px-6 py-16 text-muted-foreground" data-testid="song-detail-loading">Loading…</main>;

  return (
    <main data-testid="song-detail-page" className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <BackButton to="/library" label="Back to library" testid="song-detail-back-btn" />

      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <p className="overline-label">Song</p>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight" data-testid="song-detail-title">{song.title}</h1>
          <p className="mt-1 text-lg text-muted-foreground" data-testid="song-detail-artist">{song.artist}</p>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("song-lyrics");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="mt-3 sm:hidden inline-flex items-center gap-1 text-sm text-primary hover:underline"
            data-testid="song-detail-jump-lyrics-link-mobile"
          >
            <ArrowDown size={14} /> Jump to lyrics
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("song-lyrics");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="hidden sm:inline-flex items-center gap-1.5 h-11 px-4 rounded-full border border-border bg-background/60 text-sm text-foreground/80 hover:text-foreground hover:border-primary/50 transition-colors"
            data-testid="song-detail-jump-lyrics-btn"
          >
            <ArrowDown size={14} /> Jump to lyrics
          </button>
          <Link to={`/songs/${id}/practice`}>
            <Button className="rounded-full h-11 px-5" data-testid="song-detail-practice-btn">
              <Play size={16} className="mr-1.5" /> Practice
            </Button>
          </Link>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="song-detail-delete-btn">
                <Trash2 size={16} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this song?</AlertDialogTitle>
                <AlertDialogDescription>This can't be undone. Your practice history will be lost.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} data-testid="delete-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-3 border border-border rounded-2xl overflow-hidden">
        <div className="p-4 border-r border-border">
          <p className="overline-label">Mastery</p>
          <p className="mt-1 font-serif text-3xl">{Math.round(song.mastery || 0)}%</p>
        </div>
        <div className="p-4 border-r border-border">
          <p className="overline-label">Practices</p>
          <p className="mt-1 font-serif text-3xl">{song.practice_count || 0}</p>
        </div>
        <div className="p-4">
          <p className="overline-label">Next review</p>
          <p className="mt-1 font-serif text-lg">
            {song.next_review ? new Date(song.next_review).toLocaleDateString() : "Anytime"}
          </p>
        </div>
      </div>

      {/* Backing track */}
      <div className="mt-8">
        <BackingTrackPanel song={song} onChange={loadSong} />
      </div>

      {/* AI song analysis */}
      <div className="mt-6">
        <AiAnalysisPanel song={song} onChange={loadSong} />
      </div>

      {/* Lyrics */}
      <div id="song-lyrics" className="mt-10 scroll-mt-20">
        <div className="flex items-center justify-between">
          <p className="overline-label">Lyrics</p>
          <Link to={`/songs/${id}/edit`} className="text-sm inline-flex items-center text-muted-foreground hover:text-foreground"
                data-testid="song-detail-edit-btn">
            <Edit3 size={14} className="mr-1" /> Edit
          </Link>
        </div>
        <div className="mt-4 space-y-8">
          {(song.sections || []).length === 0 && (
            <pre className="whitespace-pre-wrap font-serif text-xl leading-relaxed">{song.lyrics}</pre>
          )}
          {(song.sections || []).map((sec, i) => (
            <section key={`${sec.name}-${i}`} data-testid={`section-${i}`}>
              <p className="overline-label mb-2">{sec.name}</p>
              <pre className="whitespace-pre-wrap font-serif text-xl md:text-2xl leading-relaxed">{sec.text}</pre>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
